import { useCallback, useId, useRef, useState } from "react";
import { useDialogA11y } from "./useDialogA11y.js";

/**
 * Las ÚNICAS opciones que `confirm()` lee. Se exporta para que la guarda
 * (`__tests__/confirmDanger.guard.test.jsx`) valide los call sites contra esta
 * lista y no contra una copia que se desincroniza.
 */
export const CONFIRM_OPTION_KEYS = [
  "title",
  "message",
  "details",
  "confirmLabel",
  "cancelLabel",
  "altLabel",
  "confirmDanger",
];

const CLAVES_VALIDAS = new Set(CONFIRM_OPTION_KEYS);

/**
 * Revienta ante una opción que no existe, en vez de ignorarla.
 *
 * El modo de fallo que esto cierra: tres call sites pasaban `variant` —el
 * nombre que usan `Badge` y `Button`— y el hook nunca lo leyó. El diálogo de
 * "Eliminar plan" y el de "Eliminar feature" abrían con el botón AZUL, el
 * mismo de una acción cualquiera. Nada fallaba: la clave se descartaba en
 * silencio y el estilo destructivo se perdía. Un `console.warn` no alcanzaba —
 * el bug vivió sin que nadie lo viera, que es lo que pasa con el aviso que
 * solo aparece en una consola que nadie tiene abierta.
 *
 * Lanza solo en desarrollo, igual que `NavBadgeCountsContext`: en dev y en la
 * suite duele donde tiene que doler, y en producción una clave de más no puede
 * ser lo que impida confirmar —o CANCELAR— el corte de combustible de un
 * vehículo. Ahí avisa por consola y el diálogo abre igual, ignorando la clave
 * sobrante.
 *
 * El aviso lleva los NOMBRES de las claves y nada más. Pasarle `options` a
 * `console.error` mandaría el título y el mensaje del diálogo —que llevan
 * cédulas, teléfonos de SIM y nombres— al breadcrumb de Sentry, que guarda los
 * argumentos crudos y los sube pegados al próximo error que se capture.
 */
function validarOpciones(options) {
  const desconocidas = Object.keys(options).filter((k) => !CLAVES_VALIDAS.has(k));
  if (desconocidas.length === 0) return;

  let mensaje =
    `confirm() recibió opciones que no existen: ${desconocidas.join(", ")}. ` +
    `Las válidas son: ${CONFIRM_OPTION_KEYS.join(", ")}.`;
  // El consejo va solo cuando aplica: si siempre estuviera, el mensaje
  // nombraría `variant` aunque la clave rota fuera otra.
  if (desconocidas.includes("variant")) {
    mensaje +=
      " Para el botón destructivo (rojo) la clave es `confirmDanger: true`;" +
      " `variant` es la prop de Badge/Button y acá no hace nada.";
  }

  if (import.meta.env.DEV) throw new Error(mensaje);
  console.error(mensaje);
}

/**
 * Hook para mostrar un modal de confirmación personalizado
 * Reemplaza completamente los alert() y confirm() del navegador
 *
 * Uso:
 *   const { confirm, ConfirmModal } = useConfirm();
 *   const ok = await confirm({ title: '...', message: '...', confirmLabel: 'OK' });
 *   if (ok) { hacer algo }
 *   En el JSX: agregar {ConfirmModal}
 *
 * Opciones — las de `CONFIRM_OPTION_KEYS` y NINGUNA otra:
 *   - `title`         encabezado del diálogo
 *   - `message`       cuerpo; respeta los saltos de línea
 *   - `details`       filas `[{ label, value }]` que van ANTES del mensaje,
 *                     etiqueta a la izquierda y valor a la derecha: los datos
 *                     de lo que se confirma (caja, fecha, montos), y el
 *                     mensaje queda para la consecuencia
 *   - `confirmLabel`  texto del botón que resuelve `true`
 *   - `cancelLabel`   texto del botón que resuelve `false`
 *   - `altLabel`      tercera acción opcional; resuelve `"alt"`
 *   - `confirmDanger` `true` pinta de ROJO el botón de confirmar. Es la ÚNICA
 *                     forma de marcar la acción como destructiva: el hook no
 *                     lee `variant`, que es la prop de `Badge`/`Button`.
 */
export function useConfirm() {
  const [state, setState] = useState({
    isOpen: false,
    title: "Confirmación",
    message: "",
    confirmLabel: "Confirmar",
    cancelLabel: "Cancelar",
    altLabel: null,
    confirmDanger: false,
  });
  const [resolver, setResolver] = useState(null);

  const confirm = (options = {}) => {
    validarOpciones(options);
    return new Promise((resolve) => {
      setState({
        isOpen: true,
        title: options.title || "Confirmación",
        message: options.message || "",
        details: options.details || null,
        confirmLabel: options.confirmLabel || "Confirmar",
        cancelLabel: options.cancelLabel || "Cancelar",
        altLabel: options.altLabel || null,
        confirmDanger: options.confirmDanger || false,
      });
      setResolver(() => resolve);
    });
  };

  const handleConfirm = () => {
    setState((prev) => ({ ...prev, isOpen: false }));
    if (resolver) resolver(true);
  };

  const handleCancel = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }));
    if (resolver) resolver(false);
  }, [resolver]);

  const handleAlt = () => {
    setState((prev) => ({ ...prev, isOpen: false }));
    if (resolver) resolver("alt");
  };

  const titleId = useId();
  // El foco de entrada va a Cancelar, no a Confirmar: este diálogo gatilla las
  // acciones destructivas del producto (cortar combustible, borrar, revocar) y
  // dejar el foco sobre la acción destructiva convierte un Enter reflejo en un
  // vehículo detenido. Escape (que cierra) resuelve `false`, la misma salida
  // segura que el botón enfocado.
  const cancelRef = useRef(null);
  const { dialogRef, dialogProps } = useDialogA11y({
    isOpen: state.isOpen,
    onClose: handleCancel,
    labelledBy: titleId,
    initialFocusRef: cancelRef,
  });

  const ConfirmModal = (
    <div>
      {state.isOpen && (
        <div className="fixed inset-0 z-[600] flex items-center justify-center scrim backdrop-blur-sm">
          <div
            ref={dialogRef}
            {...dialogProps}
            className="bg-surface-1 sheen-2 rounded-xl w-full max-w-sm shadow-elevated mx-4 border border-border focus:outline-none"
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-border">
              <h2 id={titleId} className="text-text-primary font-semibold text-lg">
                {state.title}
              </h2>
            </div>

            {/* Body */}
            <div className="px-6 py-6 space-y-4">
              {state.details?.length > 0 && (
                <dl className="divide-y divide-border rounded-lg border border-border bg-surface-2/40 text-sm">
                  {state.details.map(({ label, value }, i) => (
                    <div key={i} className="flex items-baseline justify-between gap-4 px-3 py-2">
                      <dt className="text-text-muted">{label}</dt>
                      <dd className="text-right font-medium tabular-nums text-text-primary">{value}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {state.message && (
                <p className="text-text-secondary text-sm leading-relaxed whitespace-pre-line">
                  {state.message}
                </p>
              )}
            </div>

            {/* Footer — apilado y full-width cuando hay 3 acciones (altLabel);
                horizontal alineado a la derecha en el caso binario. */}
            <div
              className={`border-t border-border ${
                state.altLabel
                  ? "flex flex-col gap-2.5 px-6 py-5"
                  : "flex items-center justify-end gap-3 px-6 py-4"
              }`}
            >
              {/* -strong bajo texto blanco: el tono de identidad da 3.98:1
                  (azul) y 3.76:1 (rojo), bajo el 4.5:1 de WCAG AA. */}
              {state.altLabel && (
                <button
                  onClick={handleAlt}
                  className="order-1 w-full py-2.5 text-sm font-semibold text-white rounded-lg transition-colors bg-primary-strong sheen-control shadow-control-primary hover:bg-primary-strong/90"
                >
                  {state.altLabel}
                </button>
              )}
              <button
                onClick={handleConfirm}
                className={`order-2 text-sm font-semibold text-white rounded-lg transition-colors ${
                  state.confirmDanger
                    ? // Cada relleno lleva la sombra de SU color. Sin esto el
                      // botón destructivo salía plano justo al lado del
                      // primario con relieve, en el mismo pie de diálogo —
                      // que es donde más se nota (ver PR #664).
                      "bg-danger-strong sheen-control shadow-control-danger hover:bg-danger-strong/90"
                    : "bg-primary-strong sheen-control shadow-control-primary hover:bg-primary-strong/90"
                } ${state.altLabel ? "w-full py-2.5" : "px-4 py-2"}`}
              >
                {state.confirmLabel}
              </button>
              <button
                ref={cancelRef}
                onClick={handleCancel}
                className={`text-sm font-medium text-text-secondary hover:text-text-primary bg-surface-2 hover:bg-surface-3 rounded-lg transition-colors ${
                  state.altLabel ? "order-3 w-full py-2.5" : "order-1 px-4 py-2"
                }`}
              >
                {state.cancelLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return { confirm, ConfirmModal };
}
